/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import * as React from 'react';
import { MyButton } from "../../components/button/MyButton"
import { Image } from "../../Images"
import { withMyTheme } from "../../theme/theme"
import { mobileCss } from '../../theme/isMobile';
import { Navigation } from '../../navigation/Navigation';

const TitleFullScreenStyle = withMyTheme(() => {
    return css`
        background-image: url(${Image.HEROBANNER});
        background-size: cover;
        display: flex;
        flex-direction: column;
        background-position: center;
        background-repeat: no-repeat;
        height: 100vh;

        ${mobileCss(`
            background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.35) 100%), url(${Image.HEROBANNER_MOBILE});
        `)}
    `;
});

const getLogoStyle = (scrollProgress: number) => withMyTheme(() => {
    // Initial and final sizes for desktop and mobile
    const desktopMaxSize = 45;
    const desktopMinSize = 15;
    const mobileMaxSize = 60;
    const mobileMinSize = 30;

    // Calculate current size based on scroll progress
    const desktopSize = desktopMaxSize - (scrollProgress * (desktopMaxSize - desktopMinSize));
    const mobileSize = mobileMaxSize - (scrollProgress * (mobileMaxSize - mobileMinSize));

    return css`
        position: fixed;
        top: 20px;
        left: 20px;
        width: ${Math.max(desktopMinSize, desktopSize)}vw;
        height: auto;
        z-index: 10;
        transition: opacity 0.3s ease-out;
        opacity: ${scrollProgress === 1 ? 0 : 1};

        ${mobileCss(`
            width: ${Math.max(mobileMinSize, mobileSize)}vw;
            top: 15px;
            left: 15px;
        `)}

        &:nth-of-type(2) {
            opacity: ${scrollProgress < 0.8 ? 1 : 0};
        }

        &:nth-of-type(1) {
            z-index: 11;
            opacity: ${scrollProgress > 0.8 && scrollProgress < 1.2 ? 1 : 0};
        }
    `;
});

const TitleContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    justify-content: center;
    gap: 20px;
    color: ${theme.palette.common.white};
    margin-top: 15vh;
    margin-left: auto;
    margin-right: 5vw;
    margin-bottom: 5vh;
    width: 45vw;
    height: 100vh;

    ${mobileCss(`
        margin-top: 0;
        margin-bottom: 5vh;
        display: flex;
        height: 100vh;
        flex-direction: column;
        justify-content: flex-end;
        text-align: center;
        align-items: center;
        width: 100vw;
    `)}
`);

const TitleStyle = withMyTheme((theme) => css`
    font-size: 3vw;
    font-weight: 300;
    margin: 0;
    font-family: ${theme.typography.h1.fontFamily};
    opacity: 0.9;

    ${mobileCss(`
        margin: 0 2vw;
        font-size: 3.5vh;
    `)}
`);

const DescriptionStyle = withMyTheme((theme) => css`
    font-size: 1.2vw;
    line-height: 1.6;
    margin: 1vh 0;
    font-family: ${theme.typography.body1.fontFamily};
    max-width: 600px;

    ${mobileCss(`
        margin: 0 10vw;
        font-size: 1.5vh;
        font-weight: 600;
    `)}
`);

const NavigationStyle = (show: boolean) => withMyTheme(() => css`
    opacity: ${show ? 1 : 0};
    pointer-events: ${show ? 'auto' : 'none'};
    transition: opacity 0.3s ease-out;
`);

const ButtonStyle = withMyTheme((theme) => css`
    &.MuiButton-contained {
        background-color: ${theme.palette.primary.main};
        &:hover {
            background-color: ${theme.palette.primary.dark};
        }
    }
    ${mobileCss(`
        width: 50vw;
        margin-bottom: 5vh;
    `)}
`);

export const Title = () => {
    const [scrollProgress, setScrollProgress] = React.useState(0);
    const [navVisible, setNavVisible] = React.useState(true);
    const lastScrollY = React.useRef(0);
    const ticking = React.useRef(false);

    React.useEffect(() => {
        // iOS Safari's toolbar can grow/shrink window.innerHeight after
        // scrolling has already stopped (no further 'scroll' event fires),
        // so nav visibility must depend only on scroll direction, never on
        // innerHeight - otherwise it can get stuck hidden on iOS.
        const update = () => {
            const viewportHeight = window.innerHeight;
            // Clamp against iOS Safari's rubber-band overscroll, which can
            // push scrollY negative at the top of the page.
            const scrollPosition = Math.max(0, window.scrollY);

            const progress = Math.min(scrollPosition / viewportHeight, 1.5);
            setScrollProgress(progress);

            const delta = scrollPosition - lastScrollY.current;
            const nearTop = scrollPosition < viewportHeight * 0.9;

            if (nearTop || delta < -5) {
                setNavVisible(true);
            } else if (delta > 5) {
                setNavVisible(false);
            }

            if (Math.abs(delta) > 5) {
                lastScrollY.current = scrollPosition;
            }

            ticking.current = false;
        };

        const handleScroll = () => {
            if (!ticking.current) {
                ticking.current = true;
                window.requestAnimationFrame(update);
            }
        };

        update();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return <div css={TitleFullScreenStyle}>    
        <img
            src={Image.LOGO_BLACK}
            alt="Agata Sawicka Logo"
            css={getLogoStyle(scrollProgress)}
        />
        <img
            src={Image.LOGO_WHITE}
            alt="Agata Sawicka Logo"
            css={getLogoStyle(scrollProgress)}
        />
        <div css={NavigationStyle(navVisible)}>
            <Navigation />
        </div>
        <div css={TitleContentStyle}>
            <h1 css={TitleStyle}>Makijaż, który podkreśli Twoje
                naturalne piękno!</h1>
            <p css={DescriptionStyle}>
                Zadbaj o swój wymarzony look i zarezerwuj wizytę
            </p>
            <MyButton
                text="NAPISZ DO MNIE"
                onClick={() => scrollToSection('contact')}
                variant="contained"
                additionalCss={ButtonStyle}
            />
        </div>
    </div>
}